import { useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, Button, TextField, Stack, Switch, IconButton, Dialog,
    DialogTitle, DialogContent, DialogActions, CircularProgress, Alert,
    MenuItem, Chip,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import PaidIcon from '@mui/icons-material/Paid';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import UndoIcon from '@mui/icons-material/Undo';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';

type AdminPlace = {
    placeId: string;
    address: string;
    name: string | null;
    kind: string;
    active: boolean;
    description: string | null;
    /** HTML SP pakalpojumu apraksts. */
    servicesDescription: string | null;
    /** Moderācijas statuss: null = pending, true = approved, false = rejected. */
    accepted: boolean | null;
    createdBy: string;
    ownerEmail: string | null;
    ownerName: string | null;
    createdAt: string;
    paidTill: string | null;
};

type PlaceKind = { kind: string; name: string };

/** Vietas statuss (active kolonna). */
type StatusFilter = 'all' | 'active' | 'inactive';
/** Moderācijas statuss (accepted kolonna: null/true/false). */
type ModerationFilter = 'all' | 'pending' | 'approved' | 'rejected';

/** Vieta ir apmaksāta, ja tai ir spēkā esoša (nākotnes) apmaksas validity (paidTill). */
function isPlacePaid(row: AdminPlace): boolean {
    return row.paidTill != null && new Date(row.paidTill).getTime() >= Date.now();
}

type RefundInfo = {
    isSuccess: boolean;
    error: string | null;
    amount: number;
    currency: string;
    providerSessionId: string | null;
    /** True, ja maksājumam ir PSP transakcija, uz kuru var izsaukt refund API. */
    canRefundViaPsp: boolean;
};

export function PlacesPage() {
    const queryClient = useQueryClient();
    const [searchInput, setSearchInput] = useState('');
    const [searchActive, setSearchActive] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [moderationFilter, setModerationFilter] = useState<ModerationFilter>('all');
    const [kindFilter, setKindFilter] = useState<string>('all');
    const [ownerFilter, setOwnerFilter] = useState('');
    const [editingPlace, setEditingPlace] = useState<AdminPlace | null>(null);
    const [payingPlace, setPayingPlace] = useState<AdminPlace | null>(null);
    const [refundingPlace, setRefundingPlace] = useState<AdminPlace | null>(null);

    const { data: places, isLoading, error } = useQuery({
        queryKey: ['admin-places', searchActive],
        queryFn: async () => {
            const resp = await api.get<{ items: AdminPlace[] }>('/api/v1/admin/places', {
                params: { search: searchActive || undefined },
            });
            return resp.data.items;
        },
    });

    const { data: kinds = [] } = useQuery({
        queryKey: ['admin-place-kinds'],
        queryFn: async () => {
            const resp = await api.get<PlaceKind[]>('/api/v1/admin/place-kinds');
            return resp.data;
        },
    });
    const kindNameById = useMemo(
        () => Object.fromEntries(kinds.map((k) => [k.kind, k.name])),
        [kinds],
    );

    /**
     * Klienta puses filtrs pār servera atgrieztajām vietām. Server jau ir izfiltrējis
     * ~500 vietas pēc adreses `search`, tāpēc atlikušos četrus filtrus varam pielikt
     * uz šī saraksta bez papildu tīkla izsaukumiem. Ja apjoms sāk pārsniegt tūkstoti,
     * pārceļam uz server-side filtriem endpoint parametros.
     */
    const filteredPlaces = useMemo(() => {
        if (!places) return [];
        const owner = ownerFilter.trim().toLowerCase();
        return places.filter((p) => {
            if (statusFilter === 'active' && !p.active) return false;
            if (statusFilter === 'inactive' && p.active) return false;

            if (moderationFilter === 'pending' && p.accepted !== null) return false;
            if (moderationFilter === 'approved' && p.accepted !== true) return false;
            if (moderationFilter === 'rejected' && p.accepted !== false) return false;

            if (kindFilter !== 'all' && p.kind !== kindFilter) return false;

            if (owner) {
                const hay = `${p.ownerEmail ?? ''} ${p.ownerName ?? ''} ${p.createdBy}`.toLowerCase();
                if (!hay.includes(owner)) return false;
            }
            return true;
        });
    }, [places, statusFilter, moderationFilter, kindFilter, ownerFilter]);

    const updateMutation = useMutation({
        mutationFn: async (payload: { placeId: string; body: Partial<AdminPlace> & { addressText?: string } }) => {
            await api.put(`/api/v1/admin/places/${payload.placeId}`, payload.body);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-places'] });
            setEditingPlace(null);
        },
    });

    const toggleActiveMutation = useMutation({
        mutationFn: async (payload: { placeId: string; active: boolean }) => {
            const path = payload.active ? 'activate' : 'deactivate';
            await api.post(`/api/v1/admin/places/${payload.placeId}/${path}`);
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-places'] }),
    });

    const addPaymentMutation = useMutation({
        mutationFn: async (payload: {
            placeId: string;
            amount: number; months: number; description: string;
        }) => {
            await api.post(`/api/v1/admin/places/${payload.placeId}/payments`, {
                amount: payload.amount,
                months: payload.months,
                currency: 'eur',
                description: payload.description,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-places'] });
            setPayingPlace(null);
        },
    });

    /**
     * Atmaksa. `viaPsp=true` liek serverim izsaukt MakeCommerce refund API (reāla naudas
     * kustība); `false` tikai ATZĪMĒ atmaksu ledger, ja naudu kustinājis admins manuāli.
     * Abos gadījumos serveris deaktivē validity, tāpēc "Apmaksāta" chip nokrīt uz "Nav".
     */
    const refundMutation = useMutation({
        mutationFn: async (payload: {
            placeId: string; amount: number; viaPsp: boolean; comment: string;
        }) => {
            const resp = await api.post<{ isSuccess: boolean; error: string | null }>(
                `/api/v1/admin/places/${payload.placeId}/refund`,
                { amount: payload.amount, viaPsp: payload.viaPsp, comment: payload.comment },
            );
            if (!resp.data.isSuccess) {
                throw new Error(resp.data.error || 'Atmaksa neizdevās.');
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-places'] });
            setRefundingPlace(null);
        },
    });

    const columns = useMemo<GridColDef<AdminPlace>[]>(() => [
        {
            field: 'name', headerName: 'Nosaukums', flex: 1, minWidth: 160,
            valueGetter: (_v, row) => row.name || row.address.split(',')[0],
        },
        { field: 'address', headerName: 'Adrese', flex: 1.5, minWidth: 240 },
        {
            field: 'kind', headerName: 'Veids', width: 140,
            valueGetter: (v?: string) => v ? (kindNameById[v] || '—') : '—',
        },
        {
            field: 'active', headerName: 'Statuss', width: 110,
            renderCell: (p) => p.value
                ? <Chip label="Aktīva" color="success" size="small" />
                : <Chip label="Slēgta" size="small" />,
        },
        {
            field: 'accepted', headerName: 'Moderācija', width: 130,
            renderCell: (p) => {
                if (p.row.accepted === null || p.row.accepted === undefined) {
                    return <Chip label="Gaida" color="warning" size="small" />;
                }
                return p.row.accepted
                    ? <Chip label="Apstiprināta" color="success" size="small" variant="outlined" />
                    : <Chip label="Noraidīta" color="error" size="small" />;
            },
        },
        {
            field: 'paid', headerName: 'Apmaksāta', width: 130, sortable: true,
            valueGetter: (_v, row) => isPlacePaid(row),
            renderCell: (p) => {
                const paid = isPlacePaid(p.row);
                // Apmaksāta + noraidīta = nauda jāatgriež - izceļam sarkanu, lai pamana.
                if (paid && p.row.accepted === false) {
                    return (
                        <Chip
                            label="Atmaksāt!"
                            color="error"
                            size="small"
                            title="Apmaksāta, bet noraidīta — jāatgriež nauda"
                        />
                    );
                }
                return paid
                    ? <Chip label="Apmaksāta" color="success" size="small" />
                    : <Chip label="Nav" size="small" variant="outlined" />;
            },
        },
        {
            field: 'paidTill', headerName: 'Apmaksāta līdz', width: 140,
            valueFormatter: (v?: string) => v ? new Date(v).toLocaleDateString('lv-LV') : '—',
        },
        {
            field: 'ownerEmail', headerName: 'Īpašnieks', width: 240,
            renderCell: (p) => {
                const name = p.row.ownerName;
                const email = p.row.ownerEmail;
                if (!name && !email) return p.row.createdBy;     // Fallback uz UID
                if (name && email) return (
                    <Stack sx={{ lineHeight: 1.2, py: 0.5 }}>
                        <span>{name}</span>
                        <span style={{ color: '#888', fontSize: 12 }}>{email}</span>
                    </Stack>
                );
                return name || email;
            },
        },
        {
            field: 'actions', headerName: '', width: 160, sortable: false, filterable: false,
            renderCell: (p) => (
                <Stack direction="row">
                    <IconButton size="small" title="Rediģēt" onClick={() => setEditingPlace(p.row)}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        title={p.row.active ? 'Deaktivēt' : 'Aktivēt'}
                        color={p.row.active ? 'warning' : 'success'}
                        onClick={() => toggleActiveMutation.mutate({
                            placeId: p.row.placeId, active: !p.row.active,
                        })}
                    >
                        {p.row.active ? <ToggleOffIcon fontSize="small" /> : <ToggleOnIcon fontSize="small" />}
                    </IconButton>
                    <IconButton
                        size="small"
                        title="Pievienot apmaksu"
                        color="primary"
                        onClick={() => setPayingPlace(p.row)}
                    >
                        <PaidIcon fontSize="small" />
                    </IconButton>
                    {/* Atmaksa - tikai apmaksātām vietām (citādi nav ko atgriezt). */}
                    {isPlacePaid(p.row) && (
                        <IconButton
                            size="small"
                            title="Atmaksāt"
                            color="error"
                            onClick={() => setRefundingPlace(p.row)}
                        >
                            <UndoIcon fontSize="small" />
                        </IconButton>
                    )}
                </Stack>
            ),
        },
    ], [kindNameById, toggleActiveMutation]);

    return (
        <Box>
            <Typography variant="h4" gutterBottom>Vietas</Typography>

            <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" useFlexGap>
                <TextField
                    size="small"
                    placeholder="Meklēt pēc adreses…"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') setSearchActive(searchInput); }}
                    sx={{ flexGrow: 1, maxWidth: 400 }}
                />
                <Button onClick={() => setSearchActive(searchInput)}>Meklēt</Button>
                <Button
                    onClick={() => { setSearchInput(''); setSearchActive(''); }}
                    disabled={!searchActive}
                >
                    Notīrīt
                </Button>
            </Stack>

            <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" useFlexGap alignItems="center">
                <TextField
                    size="small"
                    select
                    label="Statuss"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                    sx={{ minWidth: 160 }}
                >
                    <MenuItem value="all">Visi</MenuItem>
                    <MenuItem value="active">Aktīvas</MenuItem>
                    <MenuItem value="inactive">Slēgtas</MenuItem>
                </TextField>
                <TextField
                    size="small"
                    select
                    label="Moderācija"
                    value={moderationFilter}
                    onChange={(e) => setModerationFilter(e.target.value as ModerationFilter)}
                    sx={{ minWidth: 180 }}
                >
                    <MenuItem value="all">Visas</MenuItem>
                    <MenuItem value="pending">Gaida</MenuItem>
                    <MenuItem value="approved">Apstiprinātas</MenuItem>
                    <MenuItem value="rejected">Noraidītas</MenuItem>
                </TextField>
                <TextField
                    size="small"
                    select
                    label="Veids"
                    value={kindFilter}
                    onChange={(e) => setKindFilter(e.target.value)}
                    sx={{ minWidth: 180 }}
                >
                    <MenuItem value="all">Visi</MenuItem>
                    {kinds.map((k) => (
                        <MenuItem key={k.kind} value={k.kind}>{k.name}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    size="small"
                    label="Īpašnieks"
                    placeholder="E-pasts, vārds vai UID…"
                    value={ownerFilter}
                    onChange={(e) => setOwnerFilter(e.target.value)}
                    sx={{ minWidth: 240 }}
                />
                <Chip
                    label={`${filteredPlaces.length} / ${places?.length ?? 0}`}
                    variant="outlined"
                    size="small"
                />
                {(statusFilter !== 'all' || moderationFilter !== 'all' || kindFilter !== 'all' || ownerFilter) && (
                    <Button
                        size="small"
                        onClick={() => {
                            setStatusFilter('all');
                            setModerationFilter('all');
                            setKindFilter('all');
                            setOwnerFilter('');
                        }}
                    >
                        Notīrīt filtrus
                    </Button>
                )}
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>Neizdevās ielādēt sarakstu.</Alert>}

            <Box sx={{ height: 'calc(100vh - 240px)', minHeight: 400, bgcolor: 'background.paper' }}>
                <DataGrid
                    rows={filteredPlaces}
                    columns={columns}
                    getRowId={(r) => r.placeId}
                    loading={isLoading}
                    disableRowSelectionOnClick
                    initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                    pageSizeOptions={[25, 50, 100]}
                />
            </Box>

            <EditPlaceDialog
                place={editingPlace}
                kinds={kinds}
                onClose={() => setEditingPlace(null)}
                onSave={(body) => editingPlace && updateMutation.mutate({
                    placeId: editingPlace.placeId, body,
                })}
                isSaving={updateMutation.isPending}
            />

            <AddPaymentDialog
                place={payingPlace}
                onClose={() => setPayingPlace(null)}
                onSubmit={(amount, months, description) => payingPlace && addPaymentMutation.mutate({
                    placeId: payingPlace.placeId, amount, months, description,
                })}
                isSaving={addPaymentMutation.isPending}
            />

            <RefundDialog
                place={refundingPlace}
                onClose={() => setRefundingPlace(null)}
                onSubmit={(amount, viaPsp, comment) => refundingPlace && refundMutation.mutate({
                    placeId: refundingPlace.placeId, amount, viaPsp, comment,
                })}
                isSaving={refundMutation.isPending}
                error={refundMutation.error as Error | null}
            />
        </Box>
    );
}


/**
 * Atmaksas dialogs. Summa tiek pre-fill'ota no servera (samaksātais mīnus jau atgrieztais)
 * un ir rediģējama - daļēja atmaksa atļauta.
 *
 * Divas atsevišķas darbības, jo tām ir DAŽĀDAS sekas:
 *  - "Atmaksāt caur PSP" - serveris izsauc MakeCommerce refund API. REĀLA, neatgriezeniska
 *    naudas kustība. Pieejama tikai, ja maksājumam ir PSP transakcija; serverī papildus
 *    jābūt ieslēgtam MakeCommerce__RefundsEnabled.
 *  - "Atzīmēt kā atmaksātu" - tikai ledger ieraksts; naudu admins jau kustinājis manuāli.
 *
 * Abos gadījumos vieta pārstāj skaitīties apmaksāta.
 */
function RefundDialog({
    place, onClose, onSubmit, isSaving, error,
}: {
    place: AdminPlace | null;
    onClose: () => void;
    onSubmit: (amount: number, viaPsp: boolean, comment: string) => void;
    isSaving: boolean;
    error: Error | null;
}) {
    const [amount, setAmount] = useState('');
    const [comment, setComment] = useState('');

    const { data: info, isLoading } = useQuery({
        queryKey: ['admin-place-refund-info', place?.placeId],
        queryFn: async () => {
            const resp = await api.get<RefundInfo>(`/api/v1/admin/places/${place!.placeId}/refund-info`);
            return resp.data;
        },
        enabled: Boolean(place),
    });

    // Pre-fill ar servera aprēķināto atlikumu, tiklīdz tas ielādējas.
    useEffect(() => {
        if (info?.isSuccess) setAmount(info.amount.toFixed(2));
    }, [info]);

    useEffect(() => {
        if (place) setComment('');
    }, [place]);

    const parsed = parseFloat(amount);
    const amountValid = !Number.isNaN(parsed) && parsed > 0
        && (!info?.isSuccess || parsed <= info.amount + 1e-9);

    return (
        <Dialog open={Boolean(place)} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Atmaksa</DialogTitle>
            <DialogContent>
                {place && (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Alert severity="info">
                            Vieta: <strong>{place.name || place.address.split(',')[0]}</strong>
                        </Alert>

                        {isLoading && <CircularProgress size={24} />}

                        {info && !info.isSuccess && (
                            <Alert severity="warning">{info.error}</Alert>
                        )}

                        {error && <Alert severity="error">{error.message}</Alert>}

                        {info?.isSuccess && (
                            <>
                                <TextField
                                    label={`Summa (${info.currency.toUpperCase()})`}
                                    type="number"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    inputProps={{ step: '0.01', min: '0.01', max: info.amount }}
                                    helperText={`Atmaksājamais atlikums: ${info.amount.toFixed(2)} ${info.currency.toUpperCase()}`}
                                    error={amount !== '' && !amountValid}
                                    fullWidth
                                />
                                <TextField
                                    label="Komentārs"
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    placeholder="Piem., vieta noraidīta moderācijā"
                                    fullWidth
                                />
                                {!info.canRefundViaPsp && (
                                    <Alert severity="warning">
                                        Maksājumam nav PSP transakcijas - automātiska atmaksa nav
                                        iespējama. Atmaksā manuāli un atzīmē to.
                                    </Alert>
                                )}
                                <Alert severity="warning">
                                    Atmaksa caur PSP ir <strong>neatgriezeniska</strong>. Nauda aiziet
                                    atpakaļ sākotnējam maksātājam. Vieta pārstās skaitīties apmaksāta.
                                </Alert>
                            </>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Atcelt</Button>
                <Button
                    onClick={() => onSubmit(parsed, false, comment)}
                    disabled={isSaving || !amountValid}
                >
                    Atzīmēt kā atmaksātu
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    onClick={() => onSubmit(parsed, true, comment)}
                    disabled={isSaving || !amountValid || !info?.canRefundViaPsp}
                >
                    {isSaving ? <CircularProgress size={20} /> : 'Atmaksāt caur PSP'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}


function EditPlaceDialog({
    place, kinds, onClose, onSave, isSaving,
}: {
    place: AdminPlace | null;
    kinds: PlaceKind[];
    onClose: () => void;
    onSave: (body: { description: string; servicesDescription: string; kind: string; addressText: string; active: boolean }) => void;
    isSaving: boolean;
}) {
    const [description, setDescription] = useState('');
    const [servicesDescription, setServicesDescription] = useState('');
    const [kind, setKind] = useState('');
    const [addressText, setAddressText] = useState('');
    const [active, setActive] = useState(true);

    useEffect(() => {
        if (place) {
            setDescription(place.description ?? '');
            setServicesDescription(place.servicesDescription ?? '');
            setKind(place.kind);
            setAddressText(place.address);
            setActive(place.active);
        }
    }, [place]);

    return (
        <Dialog open={Boolean(place)} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Rediģēt vietu</DialogTitle>
            <DialogContent>
                {place && (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Adreses teksts"
                            value={addressText}
                            onChange={(e) => setAddressText(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Vietas veids"
                            select
                            value={kind}
                            onChange={(e) => setKind(e.target.value)}
                            fullWidth
                        >
                            {kinds.map((k) => (
                                <MenuItem key={k.kind} value={k.kind}>{k.name}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Apraksts"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            multiline
                            rows={3}
                            fullWidth
                        />
                        <TextField
                            label="Pakalpojumu apraksts (HTML)"
                            helperText="SP pamatdarbības / pakalpojumu apraksts. HTML tagi atļauti (b, br, a href). Lietotne render to caur HtmlCompat."
                            value={servicesDescription}
                            onChange={(e) => setServicesDescription(e.target.value)}
                            multiline
                            rows={4}
                            fullWidth
                        />
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Switch
                                checked={active}
                                onChange={(e) => setActive(e.target.checked)}
                            />
                            <Typography>Aktīva</Typography>
                        </Stack>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Atcelt</Button>
                <Button
                    variant="contained"
                    onClick={() => onSave({ description, servicesDescription, kind, addressText, active })}
                    disabled={isSaving}
                >
                    {isSaving ? <CircularProgress size={20} /> : 'Saglabāt'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}


function AddPaymentDialog({
    place, onClose, onSubmit, isSaving,
}: {
    place: AdminPlace | null;
    onClose: () => void;
    onSubmit: (amount: number, months: number, description: string) => void;
    isSaving: boolean;
}) {
    const [amount, setAmount] = useState('6.00');
    const [months, setMonths] = useState('12');
    const [description, setDescription] = useState('');

    return (
        <Dialog open={Boolean(place)} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Pievienot apmaksu</DialogTitle>
            <DialogContent>
                {place && (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Alert severity="info">
                            Vieta: <strong>{place.name || place.address.split(',')[0]}</strong>
                        </Alert>
                        <TextField
                            label="Summa (EUR)"
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            inputProps={{ step: '0.01', min: '0.01' }}
                            fullWidth
                        />
                        <TextField
                            label="Mēneši"
                            type="number"
                            value={months}
                            onChange={(e) => setMonths(e.target.value)}
                            inputProps={{ step: '1', min: '1' }}
                            fullWidth
                        />
                        <TextField
                            label="Komentārs"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Manuāls maksājums no admin"
                            fullWidth
                        />
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Atcelt</Button>
                <Button
                    variant="contained"
                    onClick={() => onSubmit(parseFloat(amount), parseInt(months, 10), description)}
                    disabled={isSaving || !amount || !months}
                >
                    {isSaving ? <CircularProgress size={20} /> : 'Apstiprināt'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
